/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { DestroyRef, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { joinCardView, joinDates } from '@app/app/shared/helpers/join-state.helper';
import { JoinStateDetails, PlaylistCardShort } from '@app/app/shared/models';
import { ContentJoinService, joinTargetOfItem } from '@app/app/shared/services/join/content-join.service';
import { TranslocoService } from '@ngneat/transloco';

export interface CardItemJoin {
  /** The item with the join state its last action answered with. */
  card: Signal<PlaylistCardShort>;
  busy: Signal<boolean>;
  /** Takes the step the item's join row offers. */
  act(): void;
}

/**
 * A listed item's join state, updated in place by the learner's join actions. The update only holds for the item it
 * was made on: when the page reloads its content, the fresh item wins.
 */
export function cardItemJoin(card: Signal<PlaylistCardShort>): CardItemJoin {
  const joinService = inject(ContentJoinService);
  const router = inject(Router);
  const transloco = inject(TranslocoService);
  const destroyRef = inject(DestroyRef);
  const latest = signal<{ item: PlaylistCardShort; state: JoinStateDetails } | null>(null);
  const busy = signal(false);

  const effective = computed(() => {
    const item = card();
    const answered = latest();
    return answered?.item === item ? { ...item, joinState: answered.state } : item;
  });

  return {
    card: effective,
    busy: busy.asReadonly(),
    act: () => {
      const item = card();
      const button = joinCardView(effective().joinState, joinDates(transloco.getActiveLang()))?.button;
      if (!button || busy()) {
        return;
      }
      busy.set(true);
      joinService
        .act(joinTargetOfItem(item, router.url), button.action)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((state) => {
          busy.set(false);
          if (state) {
            latest.set({ item, state });
          }
        });
    },
  };
}
